<template>
    <div v-if="showFloat" class="fixed z-888" :style="{ right: posX + 'px', bottom: posY + 'px' }"
        @mousedown="onDragStart" @touchstart.prevent="onDragStart">
        <div ref="floatWrapperRef" class="float-wrapper relative flex flex-col items-end gap-8"
            @mouseenter="showClose = true" @mouseleave="showClose = false">
            <div v-if="showBubble" class="bubbleImg cursor-pointer">
                <p>
                    {{ displayText }}
                    <span v-if="isTyping" class="cursor-blink">|</span>
                </p>
            </div>

            <div class="avatar-small relative cursor-pointer">
                <icon-svg-error v-if="showClose" class="absolute -right-10 -top-10 z-1 w-20 h-20 text-[rgba(0,0,0,0.5)]"
                    @mousedown.stop @click.stop="closeFloat" />
                <img src="https://flagos.ks3-cn-beijing.ksyuncs.com/images/ZtKV0XpP/circle_code_img.png"
                    alt="assistant" />
            </div>
        </div>
    </div>

    <div v-show="showDialog" ref="chatPanelRef" class="chat-panel" :style="chatPanelStyle"
        @mouseenter="onPanelMouseEnter" @mouseleave="onPanelMouseLeave">
        <div class="chat-panel-header flex justify-between gap-x-12 pb-12">
            <div class="text-16 font-600">
                {{ t('floatBox.panelTitle') }}
            </div>
            <div class="flex gap-x-12">
                <el-dropdown v-if="messages.length" trigger="click">
                    <span class="el-dropdown-link">
                        <span
                            class="bg-#F6F7F9 w-24 h-24 rounded-full flex items-center justify-center cursor-pointer hover:text-#3846FA">
                            <icon-svg-chat-newsocket />
                        </span>
                    </span>
                    <template #dropdown>
                        <el-dropdown-menu>
                            <el-dropdown-item @click="handleNewChat">{{ t('floatBox.newChat') }}</el-dropdown-item>
                        </el-dropdown-menu>
                    </template>
                </el-dropdown>
                <span
                    class="bg-#F6F7F9 w-24 h-24 rounded-full flex items-center justify-center cursor-pointer text-black hover:text-#3846FA"
                    @click="showDialog = false">
                    <icon-svg-chat-close />
                </span>
            </div>
        </div>
        <div class="chat-body" :style="{ height: dialogHeight + 'px' }">
            <!-- 可滚动整体区域 -->
            <div ref="messagesRef" class="chat-scroll" @wheel="onChatWheel" @scroll="onChatScroll">
                <!-- header 固定区域 -->
                <div class="chat-header">
                    <div class="flex gap-x-14 mb-14">
                        <img class="min-w-48 w-48 h-53"
                            src="https://flagos.ks3-cn-beijing.ksyuncs.com/images/ZtKV0XpP/circle_code_img.png"
                            alt="" />
                        <div class="text-#080808">
                            <div class="lh-30 text-22 font-500 flex gap-x-8 items-center">
                                {{ t('floatBox.title') }}
                            </div>
                            <p class="lh-22">{{ t('floatBox.canDo') }}</p>
                        </div>
                    </div>
                </div>
                <!-- 提示内容 -->
                <div v-if="canDoList.length" class="preset-list">
                    <div v-for="item in canDoList" :key="item.title" class="preset-card text-14"
                        @click="handlePresetClick(item.title)">
                        <div class="font-500 !text-[rgba(8,8,8,0.9)] lh-20 text-14" :style="{ color: item.color }">
                            {{ item.title }}
                        </div>
                        <div class="text-[rgba(8,8,8,0.6)] lh-20 text-14">{{ item.desc }}</div>
                    </div>
                </div>
                <div v-if="wantToAskList.length" class="mt-16">
                    <div class="text-14 text-[rgba(8,8,8,0.8)] lh-20 px-20 mb-8 font-500">{{ t('floatBox.wantToAsk') }}
                    </div>
                    <div class="want-ask-list">
                        <div class="flex items-center gap-x-4 group cursor-pointer text-#A1A6B0 hover:text-#3846FA"
                            v-for="item in wantToAskList" :key="item" @click="handlePresetClick(item)">
                            <span class="text-14 lh-18">{{ item }}</span>
                        </div>
                    </div>
                </div>
                <!-- 对话消息列表 -->
                <div class="chat-messages">
                    <template v-for="(msg, idx) in messages" :key="idx">
                        <div v-if="msg.responseStatus && msg.role === 'assistant' && (msg.content || msg.responseStatus === 'error')"
                            class="response-status my-4 flex items-center gap-4 text-14 text-[rgba(8,8,8,0.5)] lh-18 cursor-pointer select-none"
                            @click="msg.thinkingTabs?.length ? (msg.thinkingCollapsed = !msg.thinkingCollapsed) : undefined">
                            <icon-svg-chart-processing v-if="msg.responseStatus === 'processing'" />
                            <icon-svg-chat-completed v-else />
                            <span v-if="msg.responseStatus === 'processing'">{{ t('floatBox.processing') }} · {{
                                msg.responseSeconds }}s</span>
                            <span v-else-if="msg.responseStatus === 'completed'">{{ t('floatBox.completed') }} · {{
                                msg.responseSeconds }}s</span>
                            <span v-else>{{ t('floatBox.error') }} · {{ msg.responseSeconds }}s</span>
                            <icon-svg-arrow-down v-if="msg.thinkingTabs?.length"
                                class="w-12 h-12 transition-transform ml-6"
                                :class="[!msg.thinkingCollapsed ? 'rotate-360' : 'rotate-270']" />
                        </div>
                        <template v-else-if="msg.role === 'assistant' && isResponding && isAssistantEmpty">
                            <div v-if="lastAssistantLoadingMsg"
                                class="response-status my-4 flex items-center gap-4 text-14 text-[rgba(8,8,8,0.5)] lh-18">
                                <icon-svg-chart-processing />
                                <span>{{ t('floatBox.processing') }} · {{ lastAssistantLoadingMsg.responseSeconds
                                }}s</span>
                            </div>
                        </template>
                        <ThinkingProcess
                            v-if="msg.role === 'assistant' && msg.thinkingTabs && msg.thinkingTabs.length && !msg.thinkingCollapsed"
                            :tabs="msg.thinkingTabs" :active-tab-index="msg.thinkingActiveTabIndex ?? 0"
                            :status="msg.thinkingStatus ?? 'idle'" :collapsed="msg.thinkingCollapsed ?? false"
                            @update:active-tab-index="val => { msg.thinkingActiveTabIndex = val; }"
                            @update:collapsed="val => { msg.thinkingCollapsed = val; }" />
                        <div v-if="!(idx === messages.length - 1 && msg.role === 'assistant' && !msg.content && isResponding)"
                            :class="['message', msg.role]">
                            <div v-if="msg.role !== 'assistant' || msg.content" class="message-bubble !p-0">
                                <div v-if="msg.role === 'user'" class="user-text">{{ msg.content }}</div>
                                <div v-else class="assistant-text group">
                                    <MarkdownPreview :model-value="msg.content" editor-id="float-box-md" no-copy />
                                    <div class="assistant-toolbar opacity-0 group-hover:opacity-100 transition-opacity">
                                        <icon-svg-chat-cai v-if="!msg.isPreset" class="toolbar-icon"
                                            :class="{ active: msg.reaction === 'dislike' }"
                                            @click="handleDislike(msg)" />
                                        <icon-svg-chat-zan v-if="!msg.isPreset" class="toolbar-icon"
                                            :class="{ active: msg.reaction === 'like' }" @click="handleLike(msg)" />
                                        <icon-svg-chat-copy class="toolbar-icon" @click="handleCopyMsg(msg.content)" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                    <!-- <template v-if="isResponding && isAssistantEmpty">
                        <div v-if="lastAssistantLoadingMsg"
                            class="response-status my-4 flex items-center gap-4 text-14 text-[rgba(8,8,8,0.5)] lh-18">
                            <icon-svg-chart-processing />
                            <span>{{ t('floatBox.processing') }} · {{ lastAssistantLoadingMsg.responseSeconds }}s</span>
                        </div>
                        <div class="message assistant">
                            <div class="message-bubble loading-bubble">
                                <span class="dot"></span>
                                <span class="dot"></span>
                                <span class="dot"></span>
                                <span class="ml-8">
                                    {{ t('floatBox.thinking') }}
                                </span>
                            </div>
                        </div>
                    </template> -->
                </div>
            </div>
        </div>

        <!-- footer -->
        <div class="chat-footer pt-16">
            <div class="relative mb-16">
                <el-input v-model="inputValue" type="textarea" :rows="2" resize="none"
                    :placeholder="t('floatBox.placeholder')" @keydown="onInputKeydown" />
                <div class="absolute right-8px bottom-8px">
                    <div v-if="isResponding"
                        class="w-24 h-24 rounded-full bg-#080808 flex items-center justify-center cursor-pointer"
                        :disabled="!inputValue.trim()" @click="handleStop">
                        <icon-svg-chat-stop class="text-#fff" />
                    </div>
                    <div v-else
                        class="w-24 h-24 rounded-full bg-#080808 flex items-center justify-center cursor-pointer"
                        :disabled="!inputValue.trim()" @click="handleSend">
                        <icon-svg-chat-send class="text-#fff" />
                    </div>
                </div>
            </div>
            <p class="text-14 text-[rgba(8,8,8,0.7)] lh-22 text-center">{{ t('floatBox.footerTip') }}</p>
        </div>

        <!-- 缩放拖拽手柄 -->
        <div class="resize-handle resize-handle-top" @mousedown.stop="(e: MouseEvent) => onResizeStart(e, ['top'])">
        </div>
        <div class="resize-handle resize-handle-bottom"
            @mousedown.stop="(e: MouseEvent) => onResizeStart(e, ['bottom'])"></div>
        <div class="resize-handle resize-handle-left" @mousedown.stop="(e: MouseEvent) => onResizeStart(e, ['left'])">
        </div>
        <div class="resize-handle resize-handle-right" @mousedown.stop="(e: MouseEvent) => onResizeStart(e, ['right'])">
        </div>
        <!-- <div class="resize-handle resize-handle-corner"
                @mousedown.stop="(e: MouseEvent) => onResizeStart(e, ['right', 'bottom'])"></div> -->
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { getCookie } from '@/utils/cookies';
import { goLogin } from '@/utils/commonFun';
import { SocketClient } from '@/utils/socket';
import MarkdownPreview from '@/components/MarkdownPreview.vue';
import ThinkingProcess from '@/components/ThinkingProcess.vue';
import {
    getPresetPromptsApi,
    discoverExpertsApi,
    type PresetPromptItem,
    type PromptItem,
    likeMessageApi,
    unlikeMessageApi,
    dislikeMessageApi,
    undislikeMessageApi,
} from '@/apis/assistant';
import { storeToRefs } from 'pinia';
import useChatStore from '@/store/chat';
import { pushTrack, TrackEvent, FLOAT_BOX_PRESET_KEYS, LOGIN_SOURCE_KEY } from '@/utils/track';

const { t } = useI18n();
const chatStore = useChatStore();
const { messages } = storeToRefs(chatStore);
const { userId } = useStore('user');
const { currentLocale } = useStore('locale');
const showDialog = ref(false);
const showBubble = ref(false);
const showFloat = ref(true);
const showClose = ref(false);
const displayText = ref('');
const isTyping = ref(false);
const posX = ref(35);
const posY = ref(40);
const inputValue = ref('');
const isResponding = ref(false);
const messagesRef = ref<HTMLElement | null>(null);
const chatPanelRef = ref<HTMLElement | null>(null);
const userHasScrolled = ref(false);
const chatPanelLeft = ref(0);
const chatPanelTop = ref(0);

const chatPanelStyle = computed(() => ({
    position: 'fixed' as const,
    left: chatPanelLeft.value + 'px',
    top: chatPanelTop.value + 'px',
    width: dialogWidth.value + 'rem',
    zIndex: 889,
}));

let typeTimer: ReturnType<typeof setInterval> | null = null;
let hideTimer: ReturnType<typeof setTimeout> | null = null;
let responseIdleTimer: ReturnType<typeof setTimeout> | null = null;
let presetTypeTimer: ReturnType<typeof setTimeout> | null = null;
let responseTimer: ReturnType<typeof setInterval> | null = null;
let responseStartTime = 0;
let socket: SocketClient | null = null;
let sessionId = '';
const FLAGOS_EXPERT_USER_ID = import.meta.env.VITE_FLAGOS_EXPERT_USER_ID;

let isDragging = false;
let dragStartX = 0;
let dragStartY = 0;
let dragStartPosX = 0;
let dragStartPosY = 0;
let hasMoved = false;
let dragTarget: HTMLElement | null = null;
const floatWrapperRef = ref<HTMLElement | null>(null);

const MIN_DIALOG_WIDTH_REM = 34;
const MAX_DIALOG_WIDTH_PX = 1200;
const getRemPx = () => parseFloat(getComputedStyle(document.documentElement).fontSize) || 14;

const dialogWidth = ref(MIN_DIALOG_WIDTH_REM);
const dialogHeight = ref(window.innerHeight - 300);
type ResizeEdge = 'top' | 'bottom' | 'left' | 'right';
let isResizing = false;
let resizeStartX = 0;
let resizeStartY = 0;
let resizeStartWidth = 0;
let resizeStartHeight = 0;
let resizeStartDialogTop = 0;
let resizeStartDialogLeft = 0;
let resizeEdges: ResizeEdge[] = [];
let resizeBottomRoom = 0;

const SOCKET_BASE_URL = import.meta.env.VITE_SOCKET_BASE_URL;
const socketInstanceId = ref('');
const socketUrl = computed(() => (socketInstanceId.value ? `${SOCKET_BASE_URL}/${socketInstanceId.value}/chat` : ''));
const RESPONSE_IDLE_TIMEOUT = 30000;
let isFirstSocketConnect = true;
const PENDING_QUESTION_KEY = 'floatBox_pendingQuestion';

const canDoList = ref<PresetPromptItem[]>([]);
const wantToAskList = ref<string[]>([]);
const promptItems = ref<PromptItem[]>([]);

let sawToolUseResult = false;

const getLastAssistantThinking = () => {
    const last = messages.value[messages.value.length - 1];
    if (last?.role === 'assistant') return last;
    return null;
};

const ensureThinkingTab = (msg: ReturnType<typeof getLastAssistantThinking>) => {
    if (!msg) return;
    if (!msg.thinkingTabs) {
        msg.thinkingTabs = [];
        msg.thinkingStatus = 'thinking';
        msg.thinkingActiveTabIndex = 0;
    }
    if (msg.thinkingTabs.length === 0) {
        msg.thinkingTabs.push({ type: 'thinking', label: t('floatBox.thinkingTabLabel'), content: '' });
    }
};

const handleThinkingDelta = (delta: string) => {
    const msg = getLastAssistantThinking();
    if (!msg) return;
    ensureThinkingTab(msg);
    const tabs = msg.thinkingTabs!;
    let current = tabs[tabs.length - 1];
    if (current.type !== 'thinking') {
        tabs.push({ type: 'thinking', label: t('floatBox.thinkingTabLabel'), content: '' });
        current = tabs[tabs.length - 1];
    }
    current.content += delta;
    msg.thinkingActiveTabIndex = tabs.length - 1;
    scrollToBottom();
};

const handleTurnUsage = () => { };

const handleToolUseStart = (toolName: string) => {
    const msg = getLastAssistantThinking();
    if (!msg) return;
    ensureThinkingTab(msg);
    msg.thinkingTabs!.push({ type: 'tool', label: toolName, toolName, content: '' });
    msg.thinkingActiveTabIndex = msg.thinkingTabs!.length - 1;
    sawToolUseResult = false;
    scrollToBottom();
};

const handleToolUseResult = (content?: string) => {
    const msg = getLastAssistantThinking();
    if (!msg) return;
    ensureThinkingTab(msg);
    const tabs = msg.thinkingTabs!;
    const current = tabs[tabs.length - 1];
    if (current.type === 'tool' && content) {
        current.content = content;
    }
    sawToolUseResult = true;
    scrollToBottom();
};

const completeThinking = () => {
    const msg = getLastAssistantThinking();
    if (!msg?.thinkingTabs?.length) return;
    msg.thinkingStatus = 'completed';
};

const interruptThinking = () => {
    const msg = getLastAssistantThinking();
    if (!msg?.thinkingTabs?.length) return;
    if (msg.thinkingStatus !== 'completed') {
        msg.thinkingStatus = 'interrupted';
    }
};

const visualConfig: Record<string, { icon: string; color: string; bg: string }> = {
    flagos_overview: { icon: 'InfoFilled', color: '#1E64FA', bg: 'rgba(30, 100, 250, 0.1)' },
    flagos_course_guidance: { icon: 'Reading', color: '#7B61FF', bg: 'rgba(123, 97, 255, 0.1)' },
    flagos_s2_overview: { icon: 'Trophy', color: '#00B42A', bg: 'rgba(0, 180, 42, 0.1)' },
    default: { icon: 'Collection', color: '#FF7D00', bg: 'rgba(255, 125, 0, 0.1)' },
};

const mapToCanDoItem = (item: PromptItem): PresetPromptItem => {
    const visual = visualConfig[item.prompt_id] || visualConfig.default;
    return {
        prompt_id: item.prompt_id,
        title: item.prompt,
        desc: item.description,
        icon: visual.icon,
        color: visual.color,
        bg: visual.bg,
    };
};

const fetchPresetPrompts = async () => {
    try {
        const res = await getPresetPromptsApi();
        if (res?.data && Array.isArray(res.data)) {
            const items: PromptItem[] = res.data;
            promptItems.value = items;
            canDoList.value = items.filter(item => item.category === 'capability').map(mapToCanDoItem);
            wantToAskList.value = items.filter(item => item.category === 'suggestion').map(item => item.prompt);
        }
    } catch {
        // 接口失败使用空数组，不阻塞页面
    }
};

watch(currentLocale, () => {
    fetchPresetPrompts();
});

const isAssistantEmpty = computed(() => {
    const last = messages.value[messages.value.length - 1];
    return last?.role === 'assistant' && !last.content;
});

const lastAssistantLoadingMsg = computed(() => {
    const last = messages.value[messages.value.length - 1];
    if (isResponding.value && last?.role === 'assistant' && !last.content && last.responseStatus) {
        return last;
    }
    return null;
});

const closeFloat = () => {
    showFloat.value = false;
};

const onDragStart = (e: MouseEvent | TouchEvent) => {
    e.preventDefault();
    isDragging = true;
    hasMoved = false;
    dragTarget = e.target as HTMLElement;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartX = clientX;
    dragStartY = clientY;
    dragStartPosX = posX.value;
    dragStartPosY = posY.value;

    const onMove = (moveEvent: MouseEvent | TouchEvent) => {
        if (!isDragging) return;
        const moveX = 'touches' in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
        const moveY = 'touches' in moveEvent ? moveEvent.touches[0].clientY : moveEvent.clientY;
        const deltaX = dragStartX - moveX;
        const deltaY = dragStartY - moveY;

        if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
            hasMoved = true;
        }

        let newX = dragStartPosX + deltaX;
        let newY = dragStartPosY + deltaY;

        newX = Math.max(10, Math.min(newX, window.innerWidth - 100));
        newY = Math.max(10, Math.min(newY, window.innerHeight - 200));

        posX.value = newX;
        posY.value = newY;
    };

    const onUp = (upEvent: MouseEvent | TouchEvent) => {
        isDragging = false;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('touchmove', onMove);
        document.removeEventListener('touchend', onUp);

        if (!hasMoved) {
            const upTarget = upEvent.target as HTMLElement;
            if (dragTarget && floatWrapperRef.value && floatWrapperRef.value.contains(upTarget)) {
                openChat();
            }
        }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove);
    document.addEventListener('touchend', onUp);
};

const getDialogEl = () => {
    return chatPanelRef.value;
};

const setDialogLeft = (value: number) => {
    chatPanelLeft.value = value;
};

const setDialogTop = (value: number) => {
    chatPanelTop.value = value;
};

const onResizeStart = (e: MouseEvent, edges: ResizeEdge[]) => {
    e.preventDefault();
    isResizing = true;
    resizeStartX = e.clientX;
    resizeStartY = e.clientY;
    resizeStartWidth = dialogWidth.value;
    resizeStartHeight = dialogHeight.value;
    resizeEdges = edges;

    const dialog = getDialogEl();
    resizeStartDialogTop = chatPanelTop.value;
    resizeStartDialogLeft = chatPanelLeft.value;
    resizeBottomRoom = dialog ? window.innerHeight - dialog.getBoundingClientRect().bottom : 0;

    const onResizeMove = (moveEvent: MouseEvent) => {
        if (!isResizing) return;
        const deltaX = moveEvent.clientX - resizeStartX;
        const deltaY = moveEvent.clientY - resizeStartY;

        const remPx = getRemPx();
        if (resizeEdges.includes('right')) {
            const maxW = Math.max(
                MIN_DIALOG_WIDTH_REM,
                resizeStartWidth + (window.innerWidth - (resizeStartDialogLeft + resizeStartWidth * remPx)) / remPx
            );
            dialogWidth.value = Math.max(MIN_DIALOG_WIDTH_REM, Math.min(resizeStartWidth + deltaX / remPx, maxW));
        }
        if (resizeEdges.includes('left')) {
            const newWidth = Math.max(MIN_DIALOG_WIDTH_REM, Math.min(resizeStartWidth - deltaX / remPx, MAX_DIALOG_WIDTH_PX / remPx));
            setDialogLeft(resizeStartDialogLeft + (resizeStartWidth - newWidth) * remPx);
            dialogWidth.value = newWidth;
        }
        if (resizeEdges.includes('bottom')) {
            const maxH = Math.max(360, resizeStartHeight + resizeBottomRoom);
            dialogHeight.value = Math.max(360, Math.min(resizeStartHeight + deltaY, maxH));
        }
        if (resizeEdges.includes('top')) {
            const maxH = Math.max(360, resizeStartDialogTop + resizeStartHeight);
            const newHeight = Math.max(360, Math.min(resizeStartHeight - deltaY, maxH));
            setDialogTop(resizeStartDialogTop + (resizeStartHeight - newHeight));
            dialogHeight.value = newHeight;
        }
    };

    const onResizeUp = () => {
        isResizing = false;
        resizeEdges = [];
        document.removeEventListener('mousemove', onResizeMove);
        document.removeEventListener('mouseup', onResizeUp);
    };

    document.addEventListener('mousemove', onResizeMove);
    document.addEventListener('mouseup', onResizeUp);
};

const clearAllTimers = () => {
    if (typeTimer) {
        clearInterval(typeTimer);
        typeTimer = null;
    }
    if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
    }
};

const runTypewriter = (text: string, autoHide = true) => {
    clearAllTimers();
    let index = 0;
    displayText.value = '';
    showBubble.value = true;
    isTyping.value = true;

    typeTimer = setInterval(() => {
        if (index < text.length) {
            displayText.value += text[index];
            index++;
        } else {
            if (typeTimer) {
                clearInterval(typeTimer);
                typeTimer = null;
            }
            isTyping.value = false;
            if (autoHide) {
                hideTimer = setTimeout(() => {
                    showBubble.value = false;
                    displayText.value = '';
                }, 2000);
            }
        }
    }, 80);
};

const startGreetingTypewriter = () => {
    runTypewriter(t('floatBox.greeting'));
};

const onChatWheel = (e: WheelEvent) => {
    if (e.deltaY < 0) {
        userHasScrolled.value = true;
    }
};

const onChatScroll = () => {
    const el = messagesRef.value;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 10) {
        userHasScrolled.value = false;
    }
};

const onPanelMouseEnter = () => {
    document.body.style.overflow = 'hidden';
};

const onPanelMouseLeave = () => {
    document.body.style.overflow = '';
};

const scrollToBottom = () => {
    if (userHasScrolled.value) return;
    nextTick(() => {
        const el = messagesRef.value;
        if (el) el.scrollTop = el.scrollHeight;
    });
};

const buildSocketUrl = () => {
    const token = getCookie('baai-data-admin-token-key') || '';
    return `${socketUrl.value}?token=${encodeURIComponent(token)}&appcode=flagos`;
};

const fetchExpertInstance = async (): Promise<boolean> => {
    if (socketInstanceId.value) return true;
    try {
        const res = await discoverExpertsApi(FLAGOS_EXPERT_USER_ID);
        const body = (res as any)?.data;
        if (body?.code === 0 && body.data?.list) {
            const expert = body.data.list.find((item: any) => String(item.userId) === String(FLAGOS_EXPERT_USER_ID));
            if (expert?.instanceId) {
                socketInstanceId.value = expert.instanceId;
                return true;
            }
        }
    } catch {
        // 接口失败不阻塞页面
    }
    return false;
};

const isPresetResponding = () => {
    const last = messages.value[messages.value.length - 1];
    return !!(last?.isPreset && (isResponding.value || presetTypeTimer));
};

const createSocket = () => {
    if (socket || !socketUrl.value) return;
    socket = new SocketClient({
        url: buildSocketUrl(),
        heartbeatInterval: 0,
        reconnectInterval: 3000,
        maxReconnectInterval: 30000,
        maxReconnectAttempts: 5,
        onOpen: () => {
            if (isFirstSocketConnect) {
                isFirstSocketConnect = false;
                if (!messages.value.length) {
                    handleNewChat();
                }
            }
        },
        onMessage: data => {
            try {
                const parsed = JSON.parse(data);
                if (parsed.message_id) {
                    const last = messages.value[messages.value.length - 1];
                    if (last && last.role === 'assistant') {
                        last.message_id = parsed.message_id;
                    }
                }
                switch (parsed.type) {
                    case 'Resume':
                        break;
                    case 'ThinkingDelta':
                        if (!isPresetResponding()) {
                            if (parsed.delta) {
                                handleThinkingDelta(parsed.delta);
                            }
                            resetResponseIdleTimer();
                        }
                        break;
                    case 'TurnUsage':
                        if (!isPresetResponding()) {
                            handleTurnUsage();
                            resetResponseIdleTimer();
                        }
                        break;
                    case 'ToolUseStart':
                        if (!isPresetResponding()) {
                            handleToolUseStart(parsed.tool_name || 'tool');
                            resetResponseIdleTimer();
                        }
                        break;
                    case 'ToolUseResult':
                        if (!isPresetResponding()) {
                            handleToolUseResult(parsed.content);
                            resetResponseIdleTimer();
                        }
                        break;
                    case 'TextDelta':
                        if (!isPresetResponding() && parsed.delta) {
                            appendAssistantContent(parsed.delta);
                            resetResponseIdleTimer();
                        }
                        break;
                    case 'QueryFinished':
                    case 'QueryComplete':
                    case 'QueryError':
                        if (!isPresetResponding()) {
                            completeThinking();
                            finishResponse();
                        }
                        break;
                    case 'Error':
                        if (!isPresetResponding()) {
                            interruptThinking();
                            finishResponse();
                        }
                        if (parsed.session_id) {
                            ElMessage.error(parsed.message || '服务异常，请稍后重试');
                        }
                        break;
                }
            } catch {
                // 非 JSON 消息忽略
            }
        },
        onClose: () => {
            if (isPresetResponding()) return;
            if (isResponding.value) {
                failResponse();
            } else {
                finishResponse();
            }
        },
        onError: () => {
            if (isPresetResponding()) return;
            if (isResponding.value) {
                failResponse();
            } else {
                finishResponse();
            }
        },
    });
    socket.connect();
};

const appendAssistantContent = (content: string) => {
    const last = messages.value[messages.value.length - 1];
    if (last && last.role === 'assistant') {
        last.content += content;
    } else {
        messages.value.push({ role: 'assistant', content });
    }
    scrollToBottom();
};

const resetResponseIdleTimer = () => {
    if (responseIdleTimer) clearTimeout(responseIdleTimer);
    responseIdleTimer = setTimeout(() => {
        finishResponse();
    }, RESPONSE_IDLE_TIMEOUT);
};

const startResponseTimer = () => {
    responseStartTime = Date.now();
    const last = messages.value[messages.value.length - 1];
    if (!last) return;
    last.responseStatus = 'processing';
    last.responseSeconds = 0;
    if (responseTimer) clearInterval(responseTimer);
    responseTimer = setInterval(() => {
        last.responseSeconds = Math.floor((Date.now() - responseStartTime) / 1000);
    }, 1000);
};

const stopResponseTimer = () => {
    if (responseTimer) {
        clearInterval(responseTimer);
        responseTimer = null;
    }
};

const finishResponse = () => {
    if (responseIdleTimer) {
        clearTimeout(responseIdleTimer);
        responseIdleTimer = null;
    }
    if (presetTypeTimer) {
        clearTimeout(presetTypeTimer);
        presetTypeTimer = null;
    }
    stopResponseTimer();
    const last = messages.value[messages.value.length - 1];
    if (responseStartTime && last && last.role === 'assistant') {
        last.responseSeconds = Math.floor((Date.now() - responseStartTime) / 1000);
        last.responseStatus = 'completed';
    }
    responseStartTime = 0;
    isResponding.value = false;
};

const failResponse = () => {
    if (responseIdleTimer) {
        clearTimeout(responseIdleTimer);
        responseIdleTimer = null;
    }
    if (presetTypeTimer) {
        clearTimeout(presetTypeTimer);
        presetTypeTimer = null;
    }
    stopResponseTimer();
    interruptThinking();
    const last = messages.value[messages.value.length - 1];
    if (responseStartTime && last && last.role === 'assistant') {
        last.responseSeconds = Math.floor((Date.now() - responseStartTime) / 1000);
        last.responseStatus = 'error';
    }
    responseStartTime = 0;
    isResponding.value = false;
    scrollToBottom();
};

const sendQuestion = (question: string) => {
    const lastMsg = messages.value[messages.value.length - 1];
    const hasPresetHistory = lastMsg?.role === 'assistant' && lastMsg.isPreset;

    let socketContent = question;
    if (hasPresetHistory) {
        const historyParts: string[] = [];
        for (const msg of messages.value) {
            if (msg.role === 'user') {
                historyParts.push(`[用户] ${msg.content}`);
            } else if (msg.role === 'assistant' && msg.content) {
                historyParts.push(`[助手] ${msg.content}`);
            }
        }
        socketContent = `<system.message>${historyParts.join('\n')}</system.message>\n${question}`;
    }

    messages.value.push({ role: 'user', content: question });
    messages.value.push({
        role: 'assistant',
        content: '',
        thinkingTabs: [],
        thinkingStatus: 'idle',
        thinkingActiveTabIndex: 0,
        thinkingCollapsed: false,
    });
    sawToolUseResult = false;
    isResponding.value = true;
    startResponseTimer();
    inputValue.value = '';
    userHasScrolled.value = false;
    scrollToBottom();

    pushTrack(TrackEvent.FloatBoxSendQuestion, { question, sessionId, hasPresetHistory });

    createSocket();

    const payload = JSON.stringify({ type: 'Query', session_id: sessionId, content: socketContent });
    const trySend = () => {
        if (!socket) return;
        if (socket.ready.value) {
            socket.send(payload);
            resetResponseIdleTimer();
        } else if (socket.connecting.value) {
            setTimeout(trySend, 100);
        } else {
            createSocket();
            setTimeout(trySend, 100);
        }
    };
    trySend();
};

const typePresetAnswer = (content: string) => {
    if (presetTypeTimer) clearTimeout(presetTypeTimer);
    messages.value.push({ role: 'assistant', content: '', isPreset: true });
    isResponding.value = true;
    startResponseTimer();
    userHasScrolled.value = false;
    let index = 0;
    const typeChar = () => {
        if (index < content.length) {
            const last = messages.value[messages.value.length - 1];
            last.content += content[index];
            index++;
            scrollToBottom();
            presetTypeTimer = setTimeout(typeChar, 30);
        } else {
            finishResponse();
        }
    };
    typeChar();
};

const handlePresetClick = (text: string) => {
    const matchedIndex = promptItems.value.findIndex(item => item.prompt.toLowerCase() === text.toLowerCase());
    const matched = matchedIndex >= 0 ? promptItems.value[matchedIndex] : null;
    if (matched) {
        pushTrack(FLOAT_BOX_PRESET_KEYS[matchedIndex] ?? TrackEvent.FloatBoxPreset1, { promptId: matched.prompt_id });
        const socketAvailable = socket?.ready.value;
        if (isResponding.value && socketAvailable) {
            ElMessage.error(t('floatBox.waitingPrevFinish'));
            return;
        }
        if (isResponding.value) {
            finishResponse();
        }
        messages.value.push({ role: 'user', content: text });
        typePresetAnswer(matched.content);
        return;
    }
    handleSend(text);
};

const handleSend = (payload?: string | Event) => {
    const question = (typeof payload === 'string' ? payload : inputValue.value).trim();
    if (!question) return;

    const matched = promptItems.value.find(item => item.prompt.toLowerCase() === question.toLowerCase());
    if (matched) {
        messages.value.push({ role: 'user', content: question });
        inputValue.value = '';
        typePresetAnswer(matched.content);
        return;
    }

    const token = getCookie('baai-data-admin-token-key');
    if (!token) {
        sessionStorage.setItem(PENDING_QUESTION_KEY, question);
        sessionStorage.setItem(LOGIN_SOURCE_KEY, 'agent');
        goLogin();
        return;
    }

    if (isResponding.value) {
        ElMessage.error(t('floatBox.waitingPrevFinish'));
        return;
    }

    sendQuestion(question);
};

const onInputKeydown = (e: KeyboardEvent) => {
    if (e.key !== 'Enter' || e.shiftKey || e.isComposing) return;
    e.preventDefault();
    handleSend();
};

const handleStop = () => {
    finishResponse();
    socket?.close();
    socket = null;
    sessionId = '';
};

const buildReactionParams = (messageId: string) => ({
    messageId,
    operator: String(userId.value),
    sessionId,
});

const handleLike = async (msg: any) => {
    if (!msg.message_id) return;
    const params = buildReactionParams(msg.message_id);
    if (msg.reaction === 'like') {
        await unlikeMessageApi(params);
        msg.reaction = undefined;
    } else {
        await Promise.all([likeMessageApi(params), undislikeMessageApi(params)]);
        msg.reaction = 'like';
    }
};

const handleDislike = async (msg: any) => {
    if (!msg.message_id) return;
    const params = buildReactionParams(msg.message_id);
    if (msg.reaction === 'dislike') {
        await undislikeMessageApi(params);
        msg.reaction = undefined;
    } else {
        await Promise.all([dislikeMessageApi(params), unlikeMessageApi(params)]);
        msg.reaction = 'dislike';
    }
};

const handleCopyMsg = (content: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = content;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    if (copied) {
        pushTrack(TrackEvent.FloatBoxCopyAnswer);
        ElMessage.success(t('common.copySuccess'));
    } else {
        ElMessage.error(t('common.copyFail'));
    }
};

const handleNewChat = () => {
    finishResponse();
    chatStore.clearMessages();
    inputValue.value = '';

    createSocket();
    const payload = JSON.stringify({ type: 'ForgetContext', isolated: false, session_id: sessionId });
    const trySend = () => {
        if (!socket) return;
        if (socket.ready.value) {
            socket.send(payload);
        } else if (socket.connecting.value) {
            setTimeout(trySend, 100);
        } else {
            createSocket();
            setTimeout(trySend, 100);
        }
    };
    trySend();

    const { userId: storeUserId } = useStore('user');
    sessionId = `sess_${FLAGOS_EXPERT_USER_ID}_${storeUserId.value}`;
};

const updateDialogPosition = (resetHeight = true) => {
    nextTick(() => {
        const dialog = chatPanelRef.value;
        if (!dialog) return;
        const headerEl = dialog.querySelector('.chat-panel-header') as HTMLElement | null;
        const footerEl = dialog.querySelector('.chat-footer') as HTMLElement | null;
        const headerH = headerEl?.offsetHeight || 0;
        const footerH = footerEl?.offsetHeight || 0;
        const padTop = parseFloat(getComputedStyle(dialog).paddingTop) || 0;
        const padBottom = parseFloat(getComputedStyle(dialog).paddingBottom) || 0;
        const overhead = headerH + footerH + padTop + padBottom + 16;
        const bodyMaxH = Math.min(window.innerHeight - 80 - overhead, 800 - overhead);
        if (resetHeight) {
            dialogHeight.value = bodyMaxH;
        } else {
            dialogHeight.value = Math.min(dialogHeight.value, bodyMaxH);
        }
        chatPanelLeft.value = Math.max(0, window.innerWidth - dialog.offsetWidth - 35);
        chatPanelTop.value = window.innerHeight - 40 - dialogHeight.value - overhead;
    });
};

const openChat = async () => {
    if (hasMoved) return;

    pushTrack(TrackEvent.FloatBoxOpen);
    finishResponse();

    showDialog.value = true;
    showBubble.value = false;
    userHasScrolled.value = false;
    clearAllTimers();
    updateDialogPosition();
    fetchPresetPrompts();

    const token = getCookie('baai-data-admin-token-key');
    if (!token) return;

    const { userId: storeUserId } = useStore('user');
    sessionId = `sess_${FLAGOS_EXPERT_USER_ID}_${storeUserId.value}`;

    await fetchExpertInstance();
    createSocket();
};

const restorePendingQuestion = () => {
    const pending = sessionStorage.getItem(PENDING_QUESTION_KEY);
    if (!pending) return;
    sessionStorage.removeItem(PENDING_QUESTION_KEY);

    showDialog.value = true;
    showBubble.value = false;
    userHasScrolled.value = false;
    updateDialogPosition();
    fetchPresetPrompts();

    if (userId.value) {
        sessionId = `sess_${FLAGOS_EXPERT_USER_ID}_${userId.value}`;
    }

    inputValue.value = pending;
};

watch(showDialog, visible => {
    if (visible) {
        showBubble.value = false;
        clearAllTimers();
    } else {
        document.body.style.overflow = '';
        startGreetingTypewriter();
        finishResponse();
        socket?.close();
        socket = null;
        sessionId = '';
    }
});

const handleWindowResize = () => {
    posX.value = Math.max(10, Math.min(posX.value, window.innerWidth - 100));
    posY.value = Math.max(10, Math.min(posY.value, window.innerHeight - 200));
    if (showDialog.value) {
        updateDialogPosition(false);
    }
};

onMounted(() => {
    startGreetingTypewriter();
    restorePendingQuestion();
    fetchExpertInstance();
    window.addEventListener('resize', handleWindowResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleWindowResize);
    clearAllTimers();
    if (responseIdleTimer) clearTimeout(responseIdleTimer);
    finishResponse();
    socket?.close();
    socket = null;
});
</script>

<style lang="scss" scoped>
.float-wrapper {
    transition: all 0.3s ease;
    cursor: grab;
    user-select: none;

    &:active {
        cursor: grabbing;
    }
}

.bubbleImg {
    min-width: 64px;
    max-width: 300px;
    border-radius: 100px;
    background: #88ce88;
    padding: 1px;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        right: 40px;
        bottom: -7px;
        border: 12px solid red;
        border-left-color: transparent;
        border-bottom-color: transparent;
        border-right-color: #88ce88;
        border-top: 0;
    }

    &::after {
        content: '';
        position: absolute;
        right: 41px;
        bottom: -4px;
        border: 12px solid red;
        border-left-color: transparent;
        border-bottom-color: transparent;
        border-right-color: #fff;
        border-top: 0;
    }

    p {
        height: 100%;
        padding: 6px 20px;
        background: #fff;
        border-radius: 100px;
        line-height: 20px;
        color: #080808;
        font-weight: 500;
        position: relative;
    }
}

.avatar-small {
    width: 72px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
}

.cursor-blink {
    animation: blink 0.8s infinite;
}

@keyframes blink {

    0%,
    50% {
        opacity: 1;
    }

    51%,
    100% {
        opacity: 0;
    }
}

.chat-panel {
    border-radius: 24px;
    box-shadow: 1px 4px 35px 6px rgba(169, 196, 221, 0.2);
    background: #ffffff;
    padding: 16px 24px;
}

.chat-body {
    display: flex;
    flex-direction: column;
    height: 480px;
}

.chat-header {
    flex-shrink: 0;
    padding: 16px 16px 12px;
}

.chat-scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding-bottom: 16px;
}

.want-ask-list {
    padding: 16px 24px;
    border-radius: 16px;
    border: 1px solid transparent;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background:
        linear-gradient(#fff, #fff) padding-box,
        linear-gradient(135deg, rgba(255, 215, 194, 1), rgba(193, 185, 255, 1), rgba(140, 231, 255, 1)) border-box;
}

.preset-list {
    display: flex;
    flex-direction: column;
    row-gap: 10px;

    .preset-card {
        border-radius: 16px;
        background: linear-gradient(135deg, rgba(255, 215, 194, 0.2) 0%, rgba(193, 185, 255, 0.2) 50%, rgba(140, 231, 255, 0.2) 100%);
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 8px 24px;
        cursor: pointer;
        transition: all 0.2s ease;
        overflow: hidden;

        &:hover {
            border-color: #c7d2fe;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
        }
    }
}

.chat-messages {
    padding: 16px 0 0;
    display: flex;
    flex-direction: column;
}

.message {
    display: flex;
    width: 100%;

    &.user {
        justify-content: flex-end;
        margin-bottom: 8px;
    }

    &.assistant {
        justify-content: flex-start;
        margin-bottom: 20px;
    }
}

.message-bubble {
    max-width: 100%;
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 14px;
    line-height: 1.6;
    word-break: break-word;
}

.message.user .message-bubble {
    max-width: 90%;
}

.user-text {
    background: #f6f8f9;
    color: rgba(8, 8, 8, 1);
    padding: 10px 14px;
    border-radius: 8px;
}

.loading-bubble {
    display: flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 14px 18px;

    .dot {
        width: 8px;
        height: 8px;
        background: #d1d5db;
        border-radius: 50%;
        animation: loading-dot 1.4s infinite ease-in-out both;
        box-shadow: 0 0 6px rgba(156, 163, 175, 0.4);

        &:nth-child(1) {
            animation-delay: -0.32s;
            background: #9ca3af;
        }

        &:nth-child(2) {
            animation-delay: -0.16s;
        }

        &:nth-child(3) {
            animation-delay: 0s;
        }
    }
}

@keyframes loading-dot {

    0%,
    100% {
        transform: scale(0.6);
        opacity: 0.4;
    }

    50% {
        transform: scale(1);
        opacity: 1;
        background: #9ca3af;
        box-shadow: 0 0 10px rgba(156, 163, 175, 0.6);
    }
}

.chat-footer {
    :deep(.el-textarea__inner) {
        border-radius: 16px;
        padding: 12px 12px 32px;
        resize: none;
    }
}

.assistant-toolbar {
    display: flex;
    justify-content: flex-start;
    gap: 12px;
    margin-top: 10px;
}

.toolbar-icon {
    width: 16px;
    height: 16px;
    cursor: pointer;
    color: #080808;
    transition: color 0.2s;

    &:hover {
        color: #3846fa;
    }

    &.active {
        color: #3846fa;
    }
}

.resize-handle {
    position: absolute;
    z-index: 10;

    &::before {
        content: '';
        position: absolute;
        transition: border-color 0.2s;
    }
}

.resize-handle-top {
    top: -3px;
    left: 24px;
    right: 24px;
    height: 8px;
    cursor: ns-resize;
}

.resize-handle-bottom {
    bottom: -3px;
    left: 24px;
    right: 24px;
    height: 8px;
    cursor: ns-resize;
}

.resize-handle-left {
    left: -3px;
    top: 24px;
    bottom: 74px;
    width: 8px;
    cursor: ew-resize;
}

.resize-handle-right {
    right: -3px;
    top: 24px;
    bottom: 74px;
    width: 8px;
    cursor: ew-resize;
}

// .resize-handle-corner {
//     right: -3px;
//     bottom: -3px;
//     width: 16px;
//     height: 16px;
//     cursor: nwse-resize;

//     &::before {
//         right: 3px;
//         bottom: 3px;
//         width: 10px;
//         height: 10px;
//         border-right: 2px solid transparent;
//         border-bottom: 2px solid transparent;
//     }
// }
</style>
